// Native shell integration for Capacitor Android (Session 2b Task 2).
//
// Wires four native behaviors expected of a real Android app:
//   1. Hardware back button (or edge-swipe gesture on modern Android)
//      navigates in-app history; exits only at root views.
//   2. Native splash screen dismisses on "app ready" (not a timer).
//   3. External http(s) links open in the system browser, not the WebView.
//   4. Status bar style matches app chrome (dark text on cream background).
//
// Safe to import in browser builds — every entry point checks
// `Capacitor.isNativePlatform()` and short-circuits on web. Absent-Capacitor
// (desktop web + mobile web) code paths are undisturbed.

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { Browser } from '@capacitor/browser';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Share } from '@capacitor/share';
import { Network } from '@capacitor/network';

// Domains that count as "internal" — clicks to these open in the WebView.
// Everything else routes through @capacitor/browser to the system browser.
const INTERNAL_HOSTS = new Set([
    'ebiwawa.com',
    'www.ebiwawa.com',
    'app.ebiwawa.com',
]);

// Same-origin path prefixes that ARE legal/marketing static pages, not
// SPA views. On web these are served by Firebase Hosting from dist/;
// on Capacitor Android they'd trigger a full WebView reload if left to
// default handling (Capacitor serves them from app assets, but reload
// tears down the SPA state). Route these to system browser against the
// production apex URL — legal disclosures belong in the browser anyway.
const _EXTERNAL_LEGAL_PATHS = new Set([
    '/privacy',
    '/terms',
    '/help',
    '/guide',
    '/about',
    '/data-deletion',
]);

// Only the true app-root views exit on back. Every other view (Family
// Tree, Chat, Photos, Calendar, Profile, Members, Person Detail, etc.)
// walks history via window.history.back(). Matches Android Material Design
// "back walks the nav stack" convention.
const _ROOT_VIEWS = new Set([
    'feed-view',
    'dashboard-view',
]);

function isNative() {
    return typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
}

function _isAtRootView() {
    // Hash-based dispatch. SPA writes `#<viewId>` to location.hash on every
    // navigateToView call. If hash is present, that's authoritative — the
    // pathname is NOT reliable on Capacitor Android because the WebView
    // serves from `http://localhost/` so pathname is always '/' regardless
    // of view. Bug 2026-09-15: matching pathname === '/' unconditionally
    // classified every native view as root and exited from Family Tree.
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
        const viewId = hash.slice(1);
        return _ROOT_VIEWS.has(viewId);
    }
    // No hash — signed-out state (Landing or auth pages). Pathname check
    // is valid here since the app hasn't set up hash routing yet.
    const path = window.location.pathname;
    return path === '/' || path === '/login' || path === '/signup';
}

// Splash dismiss signal. Called from app.js once the first meaningful
// render is complete. Idempotent: first call hides, subsequent are no-ops.
let _splashHidden = false;
export async function dismissSplashOnReady() {
    if (!isNative() || _splashHidden) return;
    _splashHidden = true;
    try {
        await SplashScreen.hide({ fadeOutDuration: 200 });
    } catch (e) {
        console.warn('[native-shell] SplashScreen.hide failed:', e);
    }
}

// Back gesture (hardware button OR edge-swipe on modern Android) handler.
// - If a history-managed overlay is open (photo lightbox, and any future
//   overlay that registers via window._ebwOverlayCloseHandlers), route to
//   history.back() so popstate fires and runs the overlay's dedicated close
//   impl. This keeps zoom/body-scroll reset paths honest and pops the
//   synthetic history entry so back-forward stays coherent.
// - Else if a generic modal is open, call closeModal(id) directly.
// - Else if at a root view, exit the app.
// - Else walk history via window.history.back(). SPA's popstate handler
//   re-renders the previous view.
function _wireBackButton() {
    if (!isNative()) return;
    App.addListener('backButton', () => {
        // History-managed overlays go via popstate so the synthetic entry
        // pushed on open is popped cleanly. Session 2c.1 #1.
        const state = window.history.state || {};
        if (state.overlayId && window._ebwOverlayCloseHandlers && window._ebwOverlayCloseHandlers[state.overlayId]) {
            window.history.back();
            return;
        }

        const openOverlay = document.querySelector('.modal-overlay:not(.pointer-events-none)');
        if (openOverlay) {
            const modalId = openOverlay.id;
            if (modalId && typeof window.closeModal === 'function') {
                window.closeModal(modalId);
                return;
            }
            openOverlay.classList.add('pointer-events-none');
            openOverlay.classList.add('opacity-0');
            return;
        }

        if (_isAtRootView()) {
            App.exitApp();
            return;
        }

        window.history.back();
    });
}

// External-link interceptor. Attaches a capture-phase click listener on the
// document body; any anchor click whose href resolves to an external host
// gets its default prevented and routed to Browser.open() (system browser).
// Internal + same-origin clicks fall through to the WebView's default handling.
function _wireExternalLinks() {
    if (!isNative()) return;
    document.addEventListener('click', (event) => {
        const a = event.target?.closest?.('a[href]');
        if (!a) return;
        const href = a.getAttribute('href') || '';
        if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

        let url;
        try {
            url = new URL(href, window.location.href);
        } catch (e) {
            return;
        }

        // mailto:, tel:, sms: → let the OS handle via default intent.
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return;

        // Same-origin path-based navigation on native: legal + help pages
        // are Firebase-Hosting-served static files on web, but on Capacitor
        // Android navigating to /privacy would tear down the WebView + reset
        // the SPA. Route these to the production apex URL in the system
        // browser instead — legal disclosures belong there anyway.
        if (url.host === window.location.host && _EXTERNAL_LEGAL_PATHS.has(url.pathname)) {
            event.preventDefault();
            const prodUrl = 'https://ebiwawa.com' + url.pathname + (url.search || '') + (url.hash || '');
            Browser.open({ url: prodUrl, presentationStyle: 'popover' }).catch((err) => {
                console.warn('[native-shell] Browser.open (legal) failed:', err);
            });
            return;
        }

        // Same-origin or explicitly-internal host (SPA hash routes,
        // app-internal navigation) → stay in the WebView.
        if (url.host === window.location.host) return;
        if (INTERNAL_HOSTS.has(url.host.toLowerCase())) return;

        // Everything else is external — route through system browser.
        event.preventDefault();
        Browser.open({ url: url.toString(), presentationStyle: 'popover' }).catch((err) => {
            console.warn('[native-shell] Browser.open failed:', err);
            window.open(url.toString(), '_blank');
        });
    }, true); // capture=true so we see the click before app.js's own listeners
}

// Session 2c Task 3: keyboard config — global `resize` mode.
//
// Two behaviors this fixes:
//   1. Chat composer must stay visible above the keyboard. With native
//      mode (default), the keyboard overlays the WebView and the composer
//      slides under it. resize mode shrinks the WebView height so the
//      composer stays reachable.
//   2. Message list auto-scrolls to latest on keyboard open. Wired via
//      the keyboardDidShow event listener below.
//
// Watch: `resize` may cause layout jank on modal forms (Edit Profile,
// Add Person) that have their own scroll containers. If it surfaces,
// fix the modals — don't switch modes per surface (config per-surface
// requires teardown/rebuild dance).
async function _configureKeyboard() {
    if (!isNative()) return;
    try {
        await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
        await Keyboard.setAccessoryBarVisible({ isVisible: false });
    } catch (e) {
        console.warn('[native-shell] Keyboard config failed:', e);
    }
    // On keyboard open, scroll the chat message list to the latest message.
    // No-op on other views — selector doesn't match.
    Keyboard.addListener('keyboardWillShow', () => {
        const messagesList = document.getElementById('chat-messages');
        if (messagesList) {
            // rAF so browser has committed the resize before we scroll.
            requestAnimationFrame(() => {
                messagesList.scrollTop = messagesList.scrollHeight;
            });
        }
    }).catch(() => {});
}

// Session 2c Task 3: haptic helper. Fires ImpactStyle.Light for tab
// switches + primary CTAs. Restrained per brief — restraint is what
// keeps haptics feeling like punctuation, not a drumbeat.
//
// Non-native path silently no-ops (safe to call from any environment).
// Errors are swallowed — a haptic failure isn't user-visible enough to
// warrant surfacing.
export async function nativeHapticTap() {
    if (!isNative()) return;
    try {
        await Haptics.impact({ style: ImpactStyle.Light });
    } catch (_) {
        // Haptic hardware / permission edge case — silent no-op.
    }
}
// Expose on window for inline handlers that can't easily import.
if (typeof window !== 'undefined') {
    window.nativeHapticTap = nativeHapticTap;
}

// ═══════════════════════════════════════════════════════════════════════
// Session 2c Task 4: camera + client-side compression.
//
// One entry point (nativePickAndCompressImage) that:
//   - On native: opens Camera.getPhoto with prompt source (user picks
//     Camera vs Photos in a system dialog)
//   - On web: opens a transient <input type="file"> as a fallback
//   - In both paths: resizes to max 2048px long edge + exports as JPEG q80
//     via canvas.toBlob — the 3-5 MB phone photo becomes ~500-800 KB. On
//     Nigerian mobile data this is the difference between uploads that
//     complete and uploads that hang forever.
//   - Returns { blob, dataUrl, sizeMB, originalSizeMB, filename }
//
// Callers upload the returned blob wrapped in FormData. Existing upload
// endpoints unchanged — this only replaces the picker + compression layer.
//
// Permission handling: Camera.getPhoto prompts for camera + photos access
// in context (first tap of Add / Change photo, not at first launch).
// If denied, the plugin throws — we catch and surface a friendly "grant
// permission in Settings" message with a link. NOT a dead end.
// ═══════════════════════════════════════════════════════════════════════

const _COMPRESSION = {
    MAX_LONG_EDGE: 2048,  // pixels
    QUALITY: 0.82,        // JPEG quality (0-1)
    MIME: 'image/jpeg',
};

/**
 * Load an image element from a URL or data URL.
 */
function _loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.crossOrigin = 'anonymous';
        img.src = src;
    });
}

/**
 * Compress an image to max long-edge + JPEG q0.82. Accepts either a URL
 * string (webPath from Camera) or a File/Blob (from web file input).
 * Returns a Blob at the target size + a data URL for preview.
 */
async function _compressImage(source) {
    let srcUrl;
    let originalSize = 0;
    if (typeof source === 'string') {
        srcUrl = source;
        // Fetch size for reporting — Camera.getPhoto's webPath is a blob URL.
        try {
            const r = await fetch(source);
            const b = await r.blob();
            originalSize = b.size;
        } catch (_) { /* best-effort */ }
    } else if (source instanceof Blob) {
        srcUrl = URL.createObjectURL(source);
        originalSize = source.size;
    } else {
        throw new Error('Invalid image source');
    }

    try {
        const img = await _loadImage(srcUrl);
        const { width: iw, height: ih } = img;
        const longEdge = Math.max(iw, ih);
        const scale = longEdge > _COMPRESSION.MAX_LONG_EDGE
            ? _COMPRESSION.MAX_LONG_EDGE / longEdge
            : 1;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(iw * scale);
        canvas.height = Math.round(ih * scale);
        const ctx = canvas.getContext('2d');
        // High-quality resize hint. Chromium honors this.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise((resolve, reject) => {
            canvas.toBlob(
                (b) => b ? resolve(b) : reject(new Error('Canvas toBlob failed')),
                _COMPRESSION.MIME,
                _COMPRESSION.QUALITY
            );
        });

        const dataUrl = canvas.toDataURL(_COMPRESSION.MIME, _COMPRESSION.QUALITY);
        return {
            blob,
            dataUrl,
            sizeMB: (blob.size / (1024 * 1024)).toFixed(2),
            originalSizeMB: originalSize ? (originalSize / (1024 * 1024)).toFixed(2) : null,
        };
    } finally {
        if (source instanceof Blob) URL.revokeObjectURL(srcUrl);
    }
}

/**
 * Native path: use Camera plugin with prompt source (user picks Camera
 * or Photos). Web fallback: hidden file input.
 *
 * Returns { blob, dataUrl, sizeMB, originalSizeMB, filename, canceled }.
 * If user cancels the picker, resolves with { canceled: true } — caller
 * short-circuits without showing an error.
 */
export async function nativePickAndCompressImage(options = {}) {
    const { source = 'prompt' } = options;

    if (isNative()) {
        try {
            const result = await Camera.getPhoto({
                quality: 90,  // pre-compression capture quality; we re-encode at 82% below
                allowEditing: false,
                resultType: CameraResultType.Uri,
                source: source === 'camera'
                    ? CameraSource.Camera
                    : source === 'photos'
                        ? CameraSource.Photos
                        : CameraSource.Prompt,
                promptLabelHeader: 'Add a photo',
                promptLabelCancel: 'Cancel',
                promptLabelPhoto: 'From Photos',
                promptLabelPicture: 'Take Photo',
                saveToGallery: false,
            });
            // Camera returns webPath (blob URL) suitable for our compress helper.
            const compressed = await _compressImage(result.webPath);
            const ext = result.format || 'jpg';
            return {
                ...compressed,
                filename: `photo_${Date.now()}.${ext.startsWith('image/') ? 'jpg' : ext}`,
                canceled: false,
            };
        } catch (err) {
            // Capacitor Camera plugin throws with message 'User cancelled photos app'
            // or 'User cancelled photos app.' on Android when user backs out.
            const msg = (err && err.message) || String(err);
            if (/cancel/i.test(msg)) {
                return { canceled: true };
            }
            // Permission denied → surface a friendly message the caller can show.
            if (/permission/i.test(msg) || /denied/i.test(msg)) {
                throw new Error('Camera or photo access is off. Turn it on in your phone Settings > Apps > EBIWAWA > Permissions.');
            }
            throw err;
        }
    }

    // Web fallback — transient hidden file input.
    return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.style.display = 'none';
        document.body.appendChild(input);
        // Track whether the user cancels (change event won't fire on cancel).
        // Fallback via focus event on window after picker closes.
        let resolved = false;
        const cleanup = () => {
            if (input.parentNode) input.parentNode.removeChild(input);
        };
        input.addEventListener('change', async () => {
            if (resolved) return;
            resolved = true;
            const file = input.files && input.files[0];
            cleanup();
            if (!file) return resolve({ canceled: true });
            try {
                const compressed = await _compressImage(file);
                resolve({
                    ...compressed,
                    filename: file.name || `photo_${Date.now()}.jpg`,
                    canceled: false,
                });
            } catch (err) {
                reject(err);
            }
        });
        // Note: no reliable "user cancelled file picker" event on web —
        // we just leave the input hanging until change fires OR the DOM
        // is cleaned up (renderApp swap). Belt: schedule cleanup after
        // 60s so it doesn't leak.
        setTimeout(() => {
            if (!resolved) {
                cleanup();
                resolve({ canceled: true });
            }
        }, 60000);
        input.click();
    });
}

if (typeof window !== 'undefined') {
    window.nativePickAndCompressImage = nativePickAndCompressImage;
}

// ═══════════════════════════════════════════════════════════════════════
// Session 2c Task 5: system share sheet + network state.
// ═══════════════════════════════════════════════════════════════════════

/**
 * Open the system share sheet. Native uses @capacitor/share (Android + iOS
 * native share intent → user picks WhatsApp / Messages / etc.). Web uses
 * the Web Share API when available; otherwise falls back to clipboard copy
 * with a toast-style alert. Returns true if the share dispatched (or the
 * fallback copy succeeded), false if the user cancelled or nothing worked.
 *
 * @param {{title?: string, text?: string, url?: string}} payload
 */
export async function nativeShare(payload = {}) {
    const { title, text, url } = payload;
    if (isNative()) {
        try {
            await Share.share({
                title: title || 'Ebiwawa',
                text: text || '',
                url: url || '',
                dialogTitle: title || 'Share',
            });
            return true;
        } catch (err) {
            const msg = (err && err.message) || String(err);
            if (/cancel/i.test(msg)) return false;
            console.warn('[native-shell] Share.share failed:', err);
            // Fall through to web-share fallback below.
        }
    }
    // Web Share API (Chrome Android, Safari, some desktops).
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
            await navigator.share({ title, text, url });
            return true;
        } catch (err) {
            const msg = (err && err.message) || String(err);
            if (/cancel|abort/i.test(msg)) return false;
        }
    }
    // Final fallback — clipboard copy with a brief toast.
    if (url && typeof navigator !== 'undefined' && navigator.clipboard) {
        try {
            await navigator.clipboard.writeText(url);
            if (typeof window._ebwToast === 'function') {
                window._ebwToast('Link copied to clipboard');
            } else {
                alert('Link copied to clipboard');
            }
            return true;
        } catch (_) { /* noop */ }
    }
    return false;
}

if (typeof window !== 'undefined') window.nativeShare = nativeShare;

// Network state — tracks online/offline + connection quality. Consumed by
// upload buttons that need to gate on connectivity + by an optional offline
// banner (see #ebw-offline-banner below).
//
// nativeGetNetworkStatus() returns current { connected, connectionType,
// isSlow } — used by upload handlers to make a synchronous decision.
// Listener updates a body class + a banner element for cross-app UI signal.
let _networkState = { connected: true, connectionType: 'wifi', isSlow: false };

async function _initNetworkState() {
    if (isNative()) {
        try {
            const status = await Network.getStatus();
            _updateNetworkState(status);
        } catch (e) {
            console.warn('[native-shell] Network.getStatus failed:', e);
        }
        Network.addListener('networkStatusChange', _updateNetworkState).catch(() => {});
    } else if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
        // Web: use navigator.onLine + online/offline events. Connection
        // quality on web is via navigator.connection (Chrome-only, informational).
        _updateNetworkState({
            connected: navigator.onLine,
            connectionType: (navigator.connection && navigator.connection.effectiveType) || 'unknown',
        });
        window.addEventListener('online', () => _updateNetworkState({ connected: true, connectionType: 'unknown' }));
        window.addEventListener('offline', () => _updateNetworkState({ connected: false, connectionType: 'none' }));
    }
}

function _updateNetworkState({ connected, connectionType }) {
    _networkState.connected = !!connected;
    _networkState.connectionType = connectionType || 'unknown';
    // "Slow" heuristic: any 2G or slow-2g effective type on web, or
    // Capacitor's "cellular" type without further info. Not authoritative —
    // just a hint for upload UI to warn the user.
    _networkState.isSlow = /2g|slow/i.test(String(connectionType));

    if (typeof document !== 'undefined' && document.body) {
        document.body.classList.toggle('ebw-offline', !connected);
        document.body.classList.toggle('ebw-slow-net', _networkState.isSlow);
    }
    // Dispatch a custom event for any listeners that want to react.
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ebw:networkchange', {
            detail: { ..._networkState }
        }));
    }
}

/**
 * Synchronous read of the current network state. Callers use this before
 * initiating an upload — if connected is false, they should surface a
 * useful offline message rather than fire the doomed fetch.
 */
export function nativeGetNetworkStatus() {
    return { ..._networkState };
}
if (typeof window !== 'undefined') window.nativeGetNetworkStatus = nativeGetNetworkStatus;

async function _applyStatusBarTheme() {
    if (!isNative()) return;
    try {
        await StatusBar.setStyle({ style: Style.Light });
        await StatusBar.setBackgroundColor({ color: '#FAF6EE' });
    } catch (e) {
        console.warn('[native-shell] StatusBar theming failed:', e);
    }
}

// Ensure a walkable back-stack even after lastView-restored cold launches.
//
// renderApp restores lastView via navigateToView(lastView, {}, false)
// which uses replaceState — history depth stays 1. Back button then has
// nowhere to walk to. This seeder injects a Feed history entry BEFORE
// the current view if the current view isn't root, so back always walks
// to Feed as expected.
function _seedFeedBackstopIfNeeded() {
    if (!isNative()) return;
    setTimeout(() => {
        const hash = window.location.hash;
        const viewId = hash && hash.length > 1 ? hash.slice(1) : '';
        if (!viewId || _ROOT_VIEWS.has(viewId)) return;
        const currentState = window.history.state;
        const currentHash = hash;
        window.history.replaceState({ viewId: 'feed-view', params: {} }, '', '#feed-view');
        window.history.pushState(currentState, '', currentHash);
    }, 250);
}

// Public entry point. Called once from app.js at module-execution time
// on both native + web — the isNative() short-circuits keep the web
// path silent.
export function initNativeShell() {
    _markNativeBody();
    _wireBackButton();
    _wireExternalLinks();
    _applyStatusBarTheme();
    _configureKeyboard();
    _initNetworkState();
    _seedFeedBackstopIfNeeded();
    // Splash dismiss fires separately from renderApp's first meaningful-
    // render signal — see dismissSplashOnReady call in renderApp.
}

// Session 2c: set body.ebw-native so CSS can gate native-only chrome
// (hide the web hamburger + mobile bottom nav; show the native tab bar).
// Also expose isNative() result for the small number of inline handlers
// that need to branch on native-vs-web at click time.
function _markNativeBody() {
    if (!isNative()) return;
    // body may not exist yet if this runs before HTML parsing completes —
    // defer via requestAnimationFrame in that case.
    if (!document.body) {
        requestAnimationFrame(_markNativeBody);
        return;
    }
    document.body.classList.add('ebw-native');
}
